import React, { useCallback } from 'react'
import { modal, ModalParams } from '@codeleap/modals'
import { View, Text, Image, CardBase, ActionIcon, Icon } from '@/components'
import { AppImages, Theme, useAppI18N, variantProvider } from '@/app'
import { Prompt } from '@/types'
import { APIClient } from '@/services'
import { SimpleList } from '../SimpleList'
import { useQueryList } from '@/utils'

const PromptModalContent = (props: ModalParams<{}, Prompt | null>) => {
  const { request } = props

  const promptsList = APIClient.Community.promptsManager.useList()

  const listProps = useQueryList(promptsList)

  const { t } = useAppI18N()

  PromptModal.useProps(
    {
      variants: ['centered'],
      showClose: false,
      styles: {
        box: styles.box,
        innerWrapper: {
          overflowY: 'hidden',
          maxHeight: '100dvh'
        }
      }
    },
    []
  )

  const handleClose = () => {
    return request.resolve(null)
  }

  const handleContinue = () => {
    return request.resolve(undefined)
  }

  const handlePressPrompt = (prompt: Prompt) => {
    return request.resolve(prompt)
  }

  const renderPromptCard = useCallback(({ item }: { item: Prompt }) => {
    if (item?.is_hidden) return null

    return (
      <CardBase variants={['card:prompt']} onPress={() => handlePressPrompt(item)} debugName="prompt answers button">
        <View variants={['column', 'marginRight:auto']} style={{ maxWidth: '80%' }}>
          <Text variants={['p1', 'bold', 'color:neutral6']} text={item.title} />
          <Text variant={['p2', 'color:neutral6']} text={item.description} />
        </View>

        <Icon debugName="prompt:card" name="chevron-right" size={20} color={Theme.colors.light.neutral4} />
      </CardBase>
    )
  }, [])

  return (
    <View
      variants={['column', 'gap:2', 'fullWidth', 'flex', 'alignStart', 'justifyStart']}
      responsiveVariants={{
        small: ['alignCenter']
      }}
    >
      <ActionIcon
        name={'chevron-left'}
        onPress={handleClose}
        debugName={'back prompt button'}
        style={styles.back}
        variants={['minimal', 'iconSize:4']}
        responsiveVariants={{ small: ['flex'] }}
      />

      {/* TODO - change this to the admin text when its ready */}
      <CardBase variants={['card:prompt', 'bg:primary1']} debugName={`prompt default button`} onPress={handleContinue}>
        <Text variants={['h4', 'bold', 'color:primary5']} text={t('modals.prompt.defaultPrompt')} />

        <Icon debugName="prompt:card" name="chevron-right" size={20} color={Theme.colors.light.primary4} />
      </CardBase>

      <SimpleList<Prompt>
        {...listProps}
        renderItem={renderPromptCard}
        keyExtractor={(item, index) => item?.id}
        style={styles.listWrapper}
        header={
          <View variants={['row', 'alignCenter', 'justifyStart', 'gap:1']}>
            <Image source={AppImages.greenLightbulb} style={{ width: 20, height: 20 }} />
            <Text variants={['p3', 'bold']} text={t('modals.prompt.needInspiration')} />
          </View>
        }
      />
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    box: {
      maxWidth: 600,
      width: '100vw',

      // @ts-ignore
      borderRadius: theme.borderRadius.large,
      backgroundColor: theme.colors.neutral1,
      ...theme.spacing.padding(2),

      [theme.media.down('small')]: {
        minHeight: '100dvh',
        maxHeight: '100dvh',
        borderRadius: 0
      }
    },
    back: {
      position: 'absolute',
      left: theme.spacing.value(2),
      top: theme.spacing.value(2),
      display: 'none'
    },
    listWrapper: {
      minHeight: '52dvh',
      maxHeight: '52dvh',
      overflowX: 'hidden',
      overflowY: 'auto',
      borderRadius: theme.borderRadius.small,
      backgroundColor: theme.colors.neutral2,
      ...theme.spacing.padding(2),
      ...theme.spacing.gap(2),
      ...theme.presets.column,
      ...theme.presets.fullWidth,

      [theme.media.down('small')]: {
        minHeight: '78dvh',
        maxHeight: '78dvh'
      }
    }
  }),
  true
)

export const PromptModal = modal<{}, Prompt | null>().content(PromptModalContent)
